import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Stethoscope, 
    Clock, 
    ArrowRight, 
    Activity,
    AlertTriangle,
    CheckCircle,
    AlertCircle,
    History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urgencyConfig = {
    EMERGENCY: { 
        icon: AlertTriangle, 
        class: 'urgency-emergency', 
        label: 'Emergency',
        color: 'text-red-600'
    },
    URGENT: { 
        icon: AlertCircle, 
        class: 'urgency-urgent', 
        label: 'Urgent',
        color: 'text-amber-600'
    },
    ROUTINE: { 
        icon: CheckCircle, 
        class: 'urgency-routine', 
        label: 'Routine',
        color: 'text-emerald-600'
    }
};

export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [recentChecks, setRecentChecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, emergency: 0, urgent: 0, routine: 0 });

    useEffect(() => {
        fetchRecentChecks();
    }, []);

    const fetchRecentChecks = async () => {
        try {
            const response = await axios.get(`${API}/symptoms/history`);
            const checks = response.data.slice(0, 5);
            setRecentChecks(checks);
            
            const allChecks = response.data;
            setStats({
                total: allChecks.length,
                emergency: allChecks.filter(c => c.urgency_level === 'EMERGENCY').length,
                urgent: allChecks.filter(c => c.urgency_level === 'URGENT').length,
                routine: allChecks.filter(c => c.urgency_level === 'ROUTINE').length
            });
        } catch (error) {
            console.error('Failed to fetch history:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-background" data-testid="dashboard">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                        {t('welcomeBack')}, {user?.name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground text-lg">
                        {t('trackHealthAssessments') || 'Track your health assessments and get AI-powered insights.'}
                    </p>
                </div>

                {/* Quick Action */}
                <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="quick-action-card">
                    <CardContent className="p-6 sm:p-8">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                    <Stethoscope className="w-7 h-7 text-primary" />
                                </div>
                                <div>
                                    <h2 className="font-heading font-semibold text-xl">{t('newSymptomCheck') || 'New Symptom Check'}</h2>
                                    <p className="text-muted-foreground">{t('getAiPoweredAssessment') || 'Get an AI-powered health assessment'}</p>
                                </div>
                            </div>
                            <Link to="/symptom-check">
                                <Button 
                                    size="lg" 
                                    className="rounded-full shadow-lg shadow-primary/20 group"
                                    data-testid="start-check-btn"
                                >
                                    {t('startAssessment')}
                                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card data-testid="stat-total">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                    <p className="text-sm text-muted-foreground">{t('totalChecks') || 'Total Checks'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card data-testid="stat-emergency">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.emergency}</p>
                                    <p className="text-sm text-muted-foreground">{t('emergency')}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card data-testid="stat-urgent">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                    <AlertCircle className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.urgent}</p>
                                    <p className="text-sm text-muted-foreground">{t('urgent')}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card data-testid="stat-routine">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.routine}</p>
                                    <p className="text-sm text-muted-foreground">{t('routine')}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Recent Checks */}
                <Card data-testid="recent-checks-card">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="font-heading">{t('recentAssessments')}</CardTitle>
                            <CardDescription>{t('latestSymptomChecks') || 'Your latest symptom checks'}</CardDescription>
                        </div>
                        <Link to="/history">
                            <Button variant="ghost" size="sm" className="text-primary">
                                {t('viewAll')}
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <div className="spinner" />
                            </div>
                        ) : recentChecks.length === 0 ? (
                            <div className="text-center py-12">
                                <History className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                                <p className="text-muted-foreground mb-4">{t('noAssessments')}</p>
                                <Link to="/symptom-check">
                                    <Button className="rounded-full" data-testid="empty-state-btn">
                                        {t('startYourFirstCheck') || 'Start Your First Check'}
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {recentChecks.map((check, index) => {
                                    const config = urgencyConfig[check.urgency_level] || urgencyConfig.ROUTINE;
                                    const Icon = config.icon;
                                    
                                    return (
                                        <Link 
                                            key={check.id} 
                                            to={`/diagnosis/${check.id}`}
                                            className="block"
                                            data-testid={`recent-check-${index}`}
                                        >
                                            <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-muted/50 transition-colors">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.class}`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">
                                                            {check.symptoms.slice(0, 2).join(', ')}
                                                            {check.symptoms.length > 2 && ` +${check.symptoms.length - 2}`}
                                                        </p>
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <Clock className="w-3 h-3" />
                                                            {formatDate(check.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <Badge className={config.class}>
                                                    {config.label}
                                                </Badge>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
